import { PrinterIcon } from "@heroicons/react/outline";
import classNames from "classnames";
import { useRef, useState } from "react";
import { toast } from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";

import {
  Block as OpenapiBlock,
  LISBlock,
  LISCase,
  LISDocument,
  LISSpecimen,
  Specimen,
  LISTask,
} from "pwapi";

import { GrossPieceCountControl } from "shared/components/GrossPieceCount";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { procedureEnumToLabel } from "shared/utils/specimenUtils";

import {
  createBlock,
  destroyBlock,
  updateBlock,
} from "app/actions/blockActions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useBarcodePrintingCallbacks } from "hooks/useBarcodePrintingCallbacks";
import useBarcodeScanner from "hooks/useBarcodeScanner";
import useGrossingBlockDetails from "hooks/useGrossingBlockDetails";
import useGrossingMadLib from "hooks/useGrossingMadLib";
import useGrossingOnScanCallbacks from "hooks/useGrossingOnScanCallbacks";
import useIsBlockPrinting from "hooks/useIsBlockPrinting";
import {
  useSelectedLab,
  useSelectedLabStation,
} from "hooks/useSelectedLabStation";
import AccessionNumber from "ui/components/AccessionNumber";
import CasePriority from "ui/components/CasePriority";
import DotAdminLink from "ui/components/DotAdminLink";
import PatientFullName from "ui/components/PatientFullName";
import Spinner from "ui/components/Spinner";
import CaseInfo from "ui/grossing/CaseInfo";
import CheckSVG from "ui/grossing/CheckSVG";
import Grossing from "ui/grossing/grossing";
import UncheckSVG from "ui/grossing/UncheckSVG";
import { labelForBlock } from "util/blockUtils";
import getIncompleteTask from "util/getIncompleteTask";
import { blockSort } from "util/sortFunctions";

import { Specimen as SpecimenInformation } from "./GrossingStationDocument"; // TODO: rename these components
import {
  toastErrorBlockDoesNotBelong,
  toastErrorBlocksNotScannedOut,
  toastErrorIncompleteGrossingTasks,
  toastErrorSpecimenDoesNotBelong,
} from "./GrossingToastErrors";

const PrintBlockButton = ({
  block,
  pathCase,
}: {
  block: LISBlock;
  pathCase: LISCase;
}) => {
  const { printBlock } = useBarcodePrintingCallbacks();
  const isPrinting = useIsBlockPrinting(block.id || 0);

  return (
    <button
      onClick={() =>
        printBlock(block.id as number, pathCase.id as number, true)
      }
      disabled={isPrinting}
      className="Block__print"
    >
      {isPrinting ? <Spinner /> : <PrinterIcon />} Reprint
    </button>
  );
};

export const SpecimenAttributes = ({ specimen }: { specimen: LISSpecimen }) => {
  return (
    <dl>
      <div>
        <dt>Site</dt>
        <dd>{specimen.site}</dd>
      </div>

      {specimen.procedure && (
        <div>
          <dt>Procedure</dt>
          <dd>
            {procedureEnumToLabel(specimen.procedure as Specimen.procedure)}
          </dd>
        </div>
      )}
    </dl>
  );
};

const Block = ({
  block,
  onScanBlock,
  onUpdateBlock,
  showPieceCount,
  pathCase,
}: {
  block: LISBlock;
  onUpdateBlock: (id: number, blockDetails: Partial<LISBlock>) => void;
  onScanBlock: (id: number) => void;
  showPieceCount?: boolean;
  pathCase: LISCase;
}) => {
  const lisAltClickScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "lisAltClickScanning")
  );

  return (
    <article
      className="Grossed__block Object Scannable"
      onClick={(e) => {
        // TODO: this should be abstracted
        if (lisAltClickScanning && e.altKey && block.id) {
          onScanBlock(block.id);
        }
      }}
      title={`${block.id}`}
    >
      <header>
        {block.lis_state === "unprocessed" ? <UncheckSVG /> : <CheckSVG />}
        <h2 className="Label">{labelForBlock(block, pathCase)}</h2>
        {block.lis_state === "unprocessed" && (
          <PrintBlockButton {...{ block, pathCase }} />
        )}
      </header>
      {showPieceCount && (
        <GrossPieceCountControl
          block={block}
          onChange={(id: number, newValue: LISBlock.piece_count) => {
            onUpdateBlock(id, { piece_count: newValue });
          }}
        />
      )}
    </article>
  );
};

const GrossBlock = ({
  index,
  specimen,
  blockDetails,
  isSingleBlock,
  onUpdateDetails,
}: {
  index: number;
  specimen: LISSpecimen;
  blockDetails: Partial<LISBlock>;
  isSingleBlock: boolean;
  onUpdateDetails: (idx: number, blockDetails: Partial<LISBlock>) => void;
}) => {
  return (
    <article className="Grossed__block Object Scannable">
      <header>
        <h2 className="Label">{`${specimen.name}${
          isSingleBlock ? "" : index + 1
        }`}</h2>
      </header>
      <GrossPieceCountControl
        block={{ ...blockDetails, id: index }}
        onChange={(idx: number, updatedValue: LISBlock.piece_count) => {
          onUpdateDetails(idx, { piece_count: updatedValue });
        }}
      />
    </article>
  );
};

const GrossingStationSpecimen = ({
  pathCase,
  document,
  specimen,
}: {
  pathCase: LISCase;
  document: LISDocument;
  specimen: LISSpecimen;
}) => {
  const lab = useSelectedLab();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedLabStation = useSelectedLabStation();
  const [editing, setEditing] = useState(false);
  const showPieceCount = useAppSelector((state) =>
    selectFeatureFlag(state, "lisGrossPieceCount")
  );
  const allowIncompleteScanning = useAppSelector((state) =>
    selectFeatureFlag(state, "allowIncompleteScanning")
  );
  const task = getIncompleteTask(pathCase, "grossing");

  const blocks = pathCase?.blocks
    .filter((b) => b.specimen_id === specimen?.id)
    .sort(blockSort);
  const previouslyGrossedBlocks = blocks
    ?.filter((b) => b.lis_state !== "unprocessed")
    .sort(blockSort);

  const {
    onScanBlock: onScanBlockOG,
    onScanDocument,
    onScanSpecimen,
  } = useGrossingOnScanCallbacks();

  const grossingMadLib = useGrossingMadLib({
    specimen,
    complete: specimen.lis_state === "grossed",
  });

  const grossingBlockDetails = useGrossingBlockDetails({
    tokenSelections: grossingMadLib.tokenSelections,
    tokenModifiers: grossingMadLib.modifierSelections,
    specimen,
    isRawText: !!grossingMadLib.grossingRawText,
  });

  const grossingCompleted =
    specimen?.lis_state === "grossed" &&
    pathCase &&
    !pathCase.blocks.some(
      (b) => b.specimen_id === specimen.id && b.lis_state === "unprocessed"
    );
  const grossingCompletedRef = useRef(grossingCompleted);
  grossingCompletedRef.current = grossingCompleted;

  const onUpdateBlock = (id: number, updatedValues: Partial<LISBlock>) => {
    if (id) {
      dispatch(
        updateBlock({
          id,
          requestBody: updatedValues as OpenapiBlock,
        })
      );
    }
  };

  const onScanBlock = (id: number) => {
    const block = pathCase.blocks.find((b) => b.id === id);
    showPieceCount && !block?.piece_count
      ? toast.error(`${specimen.name}${block?.name || ""}: Missing piece count`)
      : onScanBlockOG(id, false).then((response: LISCase) => {
          if (pathCase && specimen.id === block?.specimen_id) {
            if (
              document &&
              !grossingCompleted &&
              grossingCompletedRef.current
            ) {
              if (pathCase.specimens.some((s) => s.lis_state !== "grossed")) {
                navigate(`/station/grossing/document/${document.id}/`);
              } else {
                navigate("/station/grossing/");
              }
            }
          } else {
            const documentId = response.documents[0].id;
            const specimenId = response.blocks.find(
              (b) => b.id === id
            )?.specimen_id;
            navigate(
              `/station/grossing/document/${documentId}/specimen/${specimenId}`
            );
          }
          return response;
        });
  };

  useBarcodeScanner(({ object_type, object_id }) => {
    toast.dismiss();
    if (object_type === "block") {
      const block = pathCase?.blocks.find(
        (b) => b.specimen_id === specimen.id && b.id === object_id
      );
      if (block) {
        onScanBlock(object_id);
      } else {
        toastErrorBlockDoesNotBelong(pathCase, specimen);
      }
    } else if (object_type === "specimen") {
      if (object_id === specimen.id) {
        toast.error(`You're already on Specimen ${specimen.name}`);
        return;
      }
      const scannedSpecimen = pathCase?.specimens.find(
        (s) => s.id === object_id
      );
      if (scannedSpecimen) {
        const blocksNotScannedOut = pathCase.blocks
          ?.filter((b) => b.specimen_id === specimen.id)
          .filter((b) => b.lis_state === LISBlock.lis_state.UNPROCESSED);

        if (allowIncompleteScanning || !blocksNotScannedOut?.length) {
          onScanSpecimen(object_id);
        } else {
          toastErrorBlocksNotScannedOut(
            pathCase,
            scannedSpecimen,
            blocksNotScannedOut
          );
        }
      } else {
        toastErrorSpecimenDoesNotBelong(pathCase);
      }
    } else if (object_type === "document") {
      if (object_id === document.id) {
        toast.error(`You're already on ${pathCase.accession}`);
        return;
      }

      const incompleteGrossingTasks = pathCase?.work_processes
        .flatMap((w) => w.tasks)
        .filter((t) => t.type === LISTask.type.GROSSING && !t.completed_at);
      const blocksNotScannedOut = pathCase.blocks?.filter(
        (b) => b.lis_state === LISBlock.lis_state.UNPROCESSED
      );

      if (
        allowIncompleteScanning ||
        (!incompleteGrossingTasks?.length && !blocksNotScannedOut?.length)
      ) {
        onScanDocument(object_id);
      } else {
        toastErrorIncompleteGrossingTasks(pathCase, incompleteGrossingTasks);
      }
    } else {
      return false;
    }
  });

  const skipAutomaticBarcodePrinting = useAppSelector((state) =>
    selectFeatureFlag(state, "skipAutomaticBarcodePrinting")
  );

  const { printBlock } = useBarcodePrintingCallbacks();

  const onClickAdd = () => {
    if (!specimen?.id || !pathCase?.id) return;
    dispatch(
      createBlock({
        parentLookupLabStationId: selectedLabStation.id,
        // name and lab_station_id in the POST body aren't actually used in the backend but the schema needs to be updated
        requestBody: {
          specimen_id: specimen.id,
          name: "",
          lab_station_id: selectedLabStation.id,
        },
      })
    ).then((response: LISCase) => {
      const asyncAutoPrint = !response._meta?.cassette_printing_task_id;
      if (!skipAutomaticBarcodePrinting && asyncAutoPrint) {
        const blockIds = (response._meta?.created?.blocks || []).concat(
          response._meta?.updated?.blocks || []
        );
        printBlock(blockIds, pathCase.id as number);
      }
      return response;
    });
  };

  const lastBlock = blocks?.at(blocks?.length - 1 || 0);

  const onClickDelete = () => {
    if (!lastBlock || lastBlock.lis_state !== "unprocessed") return null;
    if (
      window.confirm(
        `Are you sure you want to delete Block ${specimen?.name}${
          lastBlock.name || ""
        }?\nThis action cannot be undone.`
      )
    ) {
      if (!lastBlock.id || !pathCase?.id) return null;
      dispatch(
        destroyBlock({
          id: lastBlock.id,
          parentLookupLabStationId: selectedLabStation.id,
          caseId: pathCase.id,
        })
      ).then((response: LISCase) => {
        const asyncAutoPrint = !response._meta?.cassette_printing_task_id;
        if (!skipAutomaticBarcodePrinting && asyncAutoPrint) {
          const blockIds = response._meta?.updated?.blocks || [];
          printBlock(blockIds, pathCase.id as number);
        }
      });
    }
  };

  return (
    <div
      className={classNames("Station", "Split", {
        "Split--document": lab?.settings?.paperless_grossing?.active,
      })}
    >
      <main className="GrossingStation GrossingSpecimen">
        <header className="Header">
          <Link to={`/station/grossing/document/${document.id}/`}>
            <figure className="Logo">
              <img src="/logo.svg" alt="PathologyWatch" />
            </figure>
          </Link>
          {pathCase?.priority && (
            <p>
              <CasePriority priorityCase={pathCase} />
            </p>
          )}
          <p>
            <AccessionNumber accession={pathCase?.accession} />
          </p>
          <p className="Label Label--patient">
            <PatientFullName patient={pathCase?.patient} />
          </p>
          <p className="Label">
            Specimen <strong>{specimen?.name}</strong>
          </p>
          {pathCase?.id && specimen?.id && (
            <DotAdminLink caseId={pathCase.id} specimenId={specimen.id} />
          )}
        </header>

        <div>
          {specimen && (
            <SpecimenInformation
              specimen={specimen}
              onScanSpecimen={onScanSpecimen}
              pathCase={pathCase}
            />
          )}
        </div>

        {specimen && (
          <>
            {!editing && specimen.lis_state === "grossed" ? (
              <div className="Grossing__description Grossing__description--flat">
                <p>{specimen.gross_description}</p>
                <button
                  onClick={() => setEditing(true)}
                  className="Button Button--lightGrey"
                >
                  Edit
                </button>
              </div>
            ) : (
              <Grossing
                key={specimen.id}
                specimen={specimen}
                grossingMadLib={grossingMadLib}
                grossBlockDetails={grossingBlockDetails.blockDetails}
                workId={task?.work_id}
                pathCase={pathCase}
                complete={specimen.lis_state === "grossed"}
                cancel={() => setEditing(false)}
              />
            )}
          </>
        )}

        {!!previouslyGrossedBlocks?.length && (
          <div className="PreviousBlocks">
            <code>Previously Grossed "{specimen?.name}" Blocks</code>
            <div className="PreviousBlocks__blocks">
              {previouslyGrossedBlocks.map((block) => (
                <div key={block.id} className="PreviousBlocks__block">
                  <code>{labelForBlock(block, pathCase)}</code>
                  <PrintBlockButton {...{ block, pathCase }} />
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      <aside>
        <CaseInfo c={pathCase} />
        {specimen?.lis_state === "grossed" && !!blocks?.length && (
          <>
            <h4>Blocks Ready to Rack</h4>
            {blocks.map((b) => (
              <Block
                key={b.id}
                block={b}
                onScanBlock={onScanBlock}
                onUpdateBlock={onUpdateBlock}
                showPieceCount={showPieceCount}
                pathCase={pathCase}
              />
            ))}
          </>
        )}
        {specimen?.lis_state !== "grossed" &&
          !!grossingBlockDetails.blockDetails.length && (
            <>
              <h4>Additional Block Details</h4>
              {grossingBlockDetails.blockDetails.map((detail, idx) => (
                <GrossBlock
                  key={`grossed_detail_${idx}`}
                  index={idx}
                  blockDetails={detail}
                  isSingleBlock={grossingBlockDetails.blockDetails.length === 1}
                  specimen={specimen}
                  onUpdateDetails={grossingBlockDetails.manuallySetPieceCount}
                />
              ))}
            </>
          )}

        <div className="Grossed__block__actions">
          {lastBlock && (
            <button
              className="Grossed__block__delete Button Button--red"
              onClick={onClickDelete}
              disabled={
                lastBlock.lis_state !== "unprocessed" || blocks?.length === 1
              }
              title={
                blocks?.length === 1
                  ? "You cannot delete the last Block"
                  : lastBlock.lis_state !== "unprocessed"
                  ? "You cannot delete a Block once it has been racked"
                  : "Delete the most recently created Block"
              }
            >
              Delete {specimen.name}
              {lastBlock.name}
            </button>
          )}
          {specimen?.lis_state === "grossed" && (
            <button onClick={onClickAdd} className="Button Button--lightGrey">
              Add Block
            </button>
          )}
        </div>
      </aside>
    </div>
  );
};
export default GrossingStationSpecimen;
